import React, { useEffect, useState } from 'react';
import { Product } from '../../../app/models/Product';
import { Place } from '../../../app/models/Place';
import { Unit } from '../../../app/models/Unit';
import { Currency } from '../../../app/models/Currency';
import { Service } from '../../../app/models/Service';
import useWarehouseOrderItemService from '../../../app/services/hooks/useWarehouseOrderItemService';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { toast } from 'react-toastify';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import WarehouseOrderItemForm from './forms/WarehouseOrderItemForm';
import LazyLoading from '../../../components/LazyLoading';
import useSweetAlert from '../../../hooks/useSweetAlert';
import { Routes } from '../../../app/models/RouteSegment';
import useQuoteRouteSegmentService from '../../../app/services/hooks/useQuoteRouteSegmentService';
import useRouteSegmentService from '../../../app/services/hooks/useRouteSegmentService';
import { defaultWarehouseOrderCreateItemDataForm, defaultWarehouseOrderItemDataForm, WarehouseOrderItemCreateDataForm, WarehouseOrderItemDataForm } from '../../../app/models/WarehouseOrderItem';
import { Depot } from '../../../app/models/Depot';
import useWarehouseQuoteDetailService from '../../../app/services/hooks/useWarehouseQuoteDetailService';
import { QuoteDetailFormTypeEnum } from '../../../app/shared/enums';

interface Props {
    canBeEdited: boolean;
    warehouseOrderId: number;
    orderId?: number;
    currencyId: number | string;
    onSaved?: () => void;
    onCancel?: () => void;
    onError?: (message?: string) => void;
}

const WarehouseOrderItemCreate = ({
    canBeEdited,
    warehouseOrderId,
    orderId,
    currencyId,
    onSaved,
    onCancel,
    onError
}: Props) => {
    const {
        createWarehouseOrderItem,
        storeWarehouseOrderItem,
        showStorageDataToWareHouseOrderDetail,
        fetchingCreateWarehouseOrderItem,
        fetchingStoreWarehouseOrderItem
    } = useWarehouseOrderItemService();


    const [errorFields, setErrorFields] = useState<any>();

    const [warehouseOrderItemForm, setWarehouseOrderItemForm] =
        useState<WarehouseOrderItemDataForm>({
            ...defaultWarehouseOrderItemDataForm,
            // En modo unificado usar order_id; en modo legacy usar warehouse_order_id
            ...(orderId ? { order_id: orderId } : { warehouse_order_id: warehouseOrderId }),
            ['quantity']: 0,
        });
    const [warehouseOrderCreateItemForm, setWarehouseOrderCreateItemForm] =
        useState<WarehouseOrderItemCreateDataForm>({
            ...defaultWarehouseOrderCreateItemDataForm,
            ['route_id']: 0,
            ['agreed_distance']: 0
        });
    const [products, setProducts] = useState<Product[]>([]);
    const [quantityUnits, setQuantityUnits] = useState<Unit[]>([]);
    const [currencies, setCurrencies] = useState<Currency[]>([]);
    const [services, setServices] = useState<Service[]>([]);
    const [routes, setRoutes] = useState<Routes[]>([]);
    const [hasQuote, setHasQuote] = useState<boolean>(false);
    const [depots, setDepots] = useState<Depot[]>([]);
    const [units, setUnits] = useState<Unit[]>([]);
    const { storeWarehouseQuoteDetail, createWarehouseQuoteDetail, fetchingShowStorageDataWareHouseQuoteDetail, showStorageDataToWareHouseQuoteDetail } = useWarehouseQuoteDetailService();
    const [disableStorage, setDisableStorage] = useState<boolean>(false);
    const [disableRate, setDisableRate] = useState<boolean>(false);

    const isWarehouseForm =
        services.find((s) => s.id === warehouseOrderItemForm.service_id)
            ?.quote_detail_form_type === QuoteDetailFormTypeEnum.WAREHOUSE;

    useEffect(() => {
        create();
    }, []);

    // Limpiar campos exclusivos de almacenamiento cuando el servicio no es WAREHOUSE
    useEffect(() => {
        if (warehouseOrderItemForm.service_id && services.length > 0 && !isWarehouseForm) {
            setWarehouseOrderItemForm((prev) => ({
                ...prev,
                depot_id: undefined,
                free_days: 0,
            }));
        }
    }, [warehouseOrderItemForm.service_id, services.length, isWarehouseForm]);

    // useEffect(() => {
    //     if (services.length) {
    //         setWarehouseOrderItemForm({
    //             ...warehouseOrderItemForm,
    //             service_id: services[0].id
    //         });
    //     }
    // }, [services]);

    const create = () => {
        // En modo unificado usar order_id para la llamada al endpoint de create
        const createId = orderId ?? warehouseOrderId;
        createWarehouseOrderItem(createId, {
            onSuccess: (response: ServiceResponse) => {
                setProducts(response.data.products);
                setQuantityUnits(response.data.quantity_units);
                setCurrencies(response.data.currencies);
                setServices(response.data.services);
                setUnits(response.data.units);
                setDepots(response.data.depots);


            },
            onError: (response: ServiceResponse) => {
                useSweetAlert().errorAlert({
                    title: 'Error',
                    text: response.message
                });
                toast.error(response.message);
                if (onError) onError(response.message);
            },
        });
    };

    const store = () => {
        useSweetAlert().spinnerAlert('Guardando detalle pedido...', 400);
        storeWarehouseOrderItem(warehouseOrderItemForm, {
            onSuccess: (response: ServiceResponse) => {
                useSweetAlert().closeSpinnerAlert();
                toast.success(response.message);
                if (onSaved) onSaved();
            },
            onError: (response: ServiceResponse) => {
                useSweetAlert().closeSpinnerAlert();
                toast.error(response.message);
             
            },
            onFieldError: (errorFields: ServiceResponse) => {
                useSweetAlert().closeSpinnerAlert();
                setErrorFields(errorFields.data);
            }
        });
    };
    // Función para mapear los datos del API al formulario
    const mapStorageDataToForm = (storageData: any) => {
        const freeDaysData = storageData.free_days?.[0];

        if (!freeDaysData) {
            return {}; // No hay datos para mapear
        }

        return {
            // Días libres
            free_days: freeDaysData?.free_days || 0,
        };
    };

    useEffect(() => {
        // Solo para formulario WAREHOUSE: cargar datos de almacenamiento al elegir almacén/producto
        if (
            isWarehouseForm &&
            warehouseOrderItemForm.depot_id &&
            warehouseOrderItemForm.product_id &&
            currencies.length > 0 &&
            units.length > 0
        ) {
            showStorageDataToWareHouseOrderDetail(warehouseOrderId, warehouseOrderItemForm.depot_id, warehouseOrderItemForm.product_id, {
                onSuccess: (response: ServiceResponse) => {
                    setErrorFields(undefined);
                    
                    // Mapear los datos del almacenamiento al formulario
                    const mappedStorageData = mapStorageDataToForm(response.data);
                    setDisableStorage(response.data.has_storage)
                    setWarehouseOrderItemForm(prevForm => ({
                        ...prevForm,
                        ...mappedStorageData
                    }));

                },
                onError: (response: ServiceResponse) => {
                    // Si no se encuentran datos, limpiar los campos relacionados
                    setWarehouseOrderItemForm(prevForm => ({
                        ...prevForm,
                        free_days: 0,
                        comment: ''
                    }));
                }
            });
        }
    }, [isWarehouseForm, warehouseOrderItemForm.product_id, warehouseOrderItemForm.depot_id, currencies.length, units.length]);

    const cancel = () => {
        if (onCancel) onCancel();
    };

    return (
        <>
            <div className="row">
                <div className="col-12">
                    {!fetchingCreateWarehouseOrderItem ? (
                        <>
                            <WarehouseOrderItemForm
                                products={products}
                                quantityUnits={quantityUnits}
                                currencies={currencies}
                                services={services}
                                warehouseOrderItemForm={warehouseOrderItemForm}
                                setWarehouseOrderItemForm={setWarehouseOrderItemForm}
                                errorFields={errorFields}
                                // disableUpdate={false}
                                units={units}
                                depots={depots}   
                                disableStorage={disableStorage}
                                disableRate={disableRate}
                                isWarehouseForm={isWarehouseForm}
                            />
                            {/*<WarehouseOrderRatesForm*/}
                            {/*    currencies={currencies}*/}
                            {/*    quantityUnits={quantityUnits}*/}
                            {/*    warehouseOrderitemForm={warehouseOrderItemForm}*/}
                            {/*    setWarehouseOrderItemForm={setWarehouseOrderItemForm}*/}
                            {/*    errorFields={errorFields}*/}
                            {/*/> */}
                        </>
                    ) : (
                        <LazyLoading height="300" />
                    )}
                </div>
            </div>
            <hr />
            <div className="row justify-content-end">
                <div className="col-auto">
                    <ButtonCancelForm
                        callbackCancel={cancel}
                        locked={fetchingStoreWarehouseOrderItem}
                    />
                </div>
                <div className="col-auto">
                    <ButtonSaveForm
                        callbackSave={store}
                        locked={fetchingStoreWarehouseOrderItem}
                    />
                </div>
            </div>
        </>
    );
};

export default WarehouseOrderItemCreate;
